import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/apiClient";
import { ScanSearch, UploadCloud, Loader2, RotateCcw, MessageSquare, Ticket, X } from "lucide-react";
import { toast } from "sonner";

const MAX_SIZE = 8 * 1024 * 1024;

function formatContent(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="text-zinc-900">{p.slice(2, -2)}</strong>;
    }
    return p.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
}

export default function DiagnosePage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState("");
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const acceptFile = (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) {
      toast.error("Only JPG and PNG screenshots are supported");
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error("Screenshot must be under 8 MB");
      return;
    }
    setFile(f);
    setResult("");
    setDone(false);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!file || analyzing) return;
    setAnalyzing(true);
    setResult("");
    setDone(false);
    try {
      const base64 = preview.split(",")[1];
      const token = localStorage.getItem("hdp_token");
      const res = await fetch(`${API}/diagnose/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_base64: base64, mime_type: file.type }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Analysis failed. Please try again.");
        setAnalyzing(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done: rdone, value } = await reader.read();
        if (rdone) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (chunk.startsWith("data: ")) {
            const data = chunk.slice(6);
            if (data === "[DONE]") continue;
            let piece;
            try { piece = JSON.parse(data); } catch { piece = data; }
            if (typeof piece === "string" && piece.startsWith("[error]")) {
              toast.error("AI analysis error. Please try again.");
              continue;
            }
            acc += piece;
            setResult(acc);
          }
        }
      }
      setDone(true);
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult("");
    setDone(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="diagnose-page">
      <div className="mb-10 fade-up">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">AI Screenshot Diagnostics</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-zinc-900 mb-3">
          Show us the error. <span className="text-zinc-400">Get the fix.</span>
        </h1>
        <p className="text-zinc-500 max-w-xl">
          Upload a screenshot of any error — Outlook, Microsoft 365 login, printer codes, Wi-Fi issues — and our AI will identify it and walk you through the fix. No ticket needed.
        </p>
      </div>

      {!preview ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files?.[0]); }}
          className={`cursor-pointer border-2 border-dashed rounded-3xl p-16 text-center transition-all ${
            dragOver ? "border-blue-600 bg-blue-50" : "border-zinc-300 bg-white hover:border-zinc-900"
          }`}
          data-testid="diagnose-dropzone"
        >
          <UploadCloud className="w-12 h-12 text-zinc-400 mx-auto mb-5" strokeWidth={1.5} />
          <div className="font-display font-bold text-xl text-zinc-900 mb-2">Upload Screenshot for Instant Help</div>
          <p className="text-sm text-zinc-500 mb-6">Drag & drop or click to browse · JPG or PNG · max 8 MB</p>
          <span className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-6 py-3 rounded-full text-sm">
            <ScanSearch className="w-4 h-4" /> Choose screenshot
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => acceptFile(e.target.files?.[0])}
            data-testid="diagnose-file-input"
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-3xl p-6 flex items-start gap-6" data-testid="diagnose-preview-card">
            <img src={preview} alt="Screenshot preview" className="w-48 rounded-xl border border-zinc-200 object-contain" data-testid="diagnose-preview-img" />
            <div className="flex-1">
              <div className="font-bold text-zinc-900 mb-1 truncate">{file.name}</div>
              <div className="text-xs text-zinc-500 mb-5">{(file.size / 1024).toFixed(0)} KB · {file.type === "image/png" ? "PNG" : "JPG"}</div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={analyze}
                  disabled={analyzing}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-blue-700 disabled:opacity-50 transition"
                  data-testid="diagnose-analyze-btn"
                >
                  {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><ScanSearch className="w-4 h-4" /> Diagnose this screenshot</>}
                </button>
                <button
                  onClick={reset}
                  disabled={analyzing}
                  className="inline-flex items-center gap-2 border-2 border-zinc-200 text-zinc-600 font-bold px-5 py-3 rounded-full text-sm hover:border-zinc-900 hover:text-zinc-900 disabled:opacity-50 transition"
                  data-testid="diagnose-reset-btn"
                >
                  <X className="w-4 h-4" /> Remove
                </button>
              </div>
            </div>
          </div>

          {(result || analyzing) && (
            <div className="bg-white border border-zinc-200 rounded-3xl p-8" data-testid="diagnose-result">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-zinc-900 rounded-xl flex items-center justify-center">
                  <ScanSearch className="w-4 h-4 text-white" />
                </div>
                <div className="font-display font-bold text-lg text-zinc-900">AI Diagnosis</div>
                {analyzing && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
              </div>
              <div className="text-[15px] text-zinc-700 leading-relaxed" data-testid="diagnose-result-text">
                {result ? formatContent(result) : <span className="text-zinc-400">Reading your screenshot…</span>}
              </div>

              {done && (
                <div className="mt-8 pt-6 border-t border-zinc-100 flex flex-wrap gap-3" data-testid="diagnose-followups">
                  <button
                    onClick={reset}
                    className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-5 py-2.5 rounded-full text-sm hover:bg-zinc-800 transition"
                    data-testid="diagnose-another-btn"
                  >
                    <RotateCcw className="w-4 h-4" /> Diagnose another
                  </button>
                  <Link
                    to="/app/chat"
                    className="inline-flex items-center gap-2 border-2 border-zinc-200 text-zinc-700 font-bold px-5 py-2.5 rounded-full text-sm hover:border-zinc-900 transition"
                    data-testid="diagnose-to-chat"
                  >
                    <MessageSquare className="w-4 h-4" /> Ask the AI more
                  </Link>
                  <Link
                    to="/app/tickets/new"
                    className="inline-flex items-center gap-2 border-2 border-zinc-200 text-zinc-700 font-bold px-5 py-2.5 rounded-full text-sm hover:border-zinc-900 transition"
                    data-testid="diagnose-to-ticket"
                  >
                    <Ticket className="w-4 h-4" /> Still stuck? File a ticket
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-10 grid sm:grid-cols-4 gap-3 text-center">
        {["Outlook errors", "Microsoft 365 login", "Printer error codes", "Wi-Fi issues"].map((t) => (
          <div key={t} className="bg-white border border-zinc-200 rounded-xl px-3 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wider">
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}
